import React, { useState, useEffect, useCallback } from "react";
import ScanListItem from "./ScanListItem";
import PushProgressModal from "./PushProgressModal";
import CopyProgressModal from "./CopyProgressModal";
import { ENVIRONMENTS } from "../../pages/common/ScansByEnvironment";
import ConfirmationModal from "./ConfirmationModal";
import DeleteAllIssuesModal from "./DeleteAllIssuesModal";
import Tooltip from "./Tooltip";
import NotificationRecipientsModal from "./NotificationRecipientsModal";
import NotificationEmailPreviewModal from "./NotificationEmailPreviewModal";
import { captureException } from "../../app/sentry";
import {
  formatMutationError,
  shouldReportMutationFailure,
  normalizeWebsiteId,
  isScanAlreadyExistsError,
  toCaptureError,
} from "../../app/utils/mutationError";
import { runBulkIssueDeletes } from "../../app/utils/bulkDeleteIssues";

// console → staging
import {
  useCreateConsoleScanMutation,
  useConsolidateConsoleScanMutation,
} from "../../app/api/console/consoleScansApiSlice";
import {
  useCreateConsoleIssueMutation,
  useDeleteConsoleIssueMutation,
} from "../../app/api/console/consoleIssuesApiSlice";
import {
  useCreatePlatformIssueMutation,
  useUpsertPlatformIssueMutation,
  useDeletePlatformIssueMutation,
  useBulkUpsertPlatformIssuesMutation,
} from "../../app/api/platform/platformIssuesApiSlice";

// staging → production
import {
  useCreatePlatformScanMutation,
  useUpdatePlatformScanMutation,
  useDeletePlatformScanMutation,
  useMergePlatformScansMutation,
} from "../../app/api/platform/platformScansApiSlice";
import { useDeletePlatformActivityRunMutation } from "../../app/api/platform/platformActivityApiSlice";
import {
  usePushScanJourneysToProdMutation,
  useFinalizeProdPushMutation,
} from "../../app/api/console/scanJourneySyncApiSlice";

// Main ScanList component
const ScanList = ({
  scans = [],
  onScanSelect,
  onScanExpand,
  selectedScan,
  onDeleteIssues,
  isLoading,
  environment,
  stagingWebsiteId: stagingWebsiteIdProp,
  productionWebsiteId: productionWebsiteIdProp,
  onCreateScan,
}) => {
  // Paste/DB noise (trailing tabs/spaces) breaks Platform FK lookups (404).
  const stagingWebsiteId = normalizeWebsiteId(stagingWebsiteIdProp);
  const productionWebsiteId = normalizeWebsiteId(productionWebsiteIdProp);

  // Normalize scan IDs to ensure both id and scan_id are present
  const getPrimaryScanId = (scan) => {
    if (!scan) {
      console.error("getPrimaryScanId: scan is undefined or null");
      return null;
    }
    return scan.id || scan.scan_id || scan.console_scan_id;
  };
  const normalizeScans = (scansList) => {
    return scansList.map((scan) => ({
      ...scan,
      id: getPrimaryScanId(scan),
      scan_id: scan.scan_id || scan.id || scan.console_scan_id,
    }));
  };

  const [sortMode, setSortMode] = useState("recent");

  /* --- hooks for pushing --- */
  const [createConsoleScan] = useCreateConsoleScanMutation();
  const [createConsoleIssue] = useCreateConsoleIssueMutation();
  const [deleteConsoleIssue] = useDeleteConsoleIssueMutation();
  const [createPlatformScan] = useCreatePlatformScanMutation();
  const [updatePlatformScan] = useUpdatePlatformScanMutation();
  const [createPlatformIssue] = useCreatePlatformIssueMutation();
  const [upsertPlatformIssue] = useUpsertPlatformIssueMutation();
  const [deletePlatformIssue] = useDeletePlatformIssueMutation();
  const [bulkUpsertPlatformIssues] = useBulkUpsertPlatformIssuesMutation();
  const [deletePlatformScan] = useDeletePlatformScanMutation();
  const [deletePlatformActivityRun] = useDeletePlatformActivityRunMutation();
  const [mergePlatformScans] = useMergePlatformScansMutation();
  const [pushScanJourneysToProd] = usePushScanJourneysToProdMutation();
  const [finalizeProdPush] = useFinalizeProdPushMutation();

  /* --- hook for consolidation --- */
  const [consolidateConsoleScan] = useConsolidateConsoleScanMutation();

  // State to track which scans / issues are selected for push
  const [pushSelections, setPushSelections] = useState({});
  const [showPushRecipientsModal, setShowPushRecipientsModal] = useState(false);

  // CUS-1353: the push picker preselects the pushed run's agent recipients
  // plus its launcher. On production the run becomes a legacy-run-* with
  // neither, so both come from the staging row. Several runs share one
  // recipient list, so they get an agent only when they agree, and no
  // launcher (theirs may differ).
  const getPushRecipientContext = () => {
    const pushed = Object.keys(pushSelections)
      .map(
        (key) =>
          scans.find((scan) => getPrimaryScanId(scan) === key) ||
          pushSelections[key]?.scan
      )
      .filter(Boolean);
    if (pushed.length === 1) {
      return {
        agentSlug: pushed[0].activity_run_agent_slug || undefined,
        stagingLauncherUserId:
          pushed[0].activity_run_launcher_user_id || undefined,
      };
    }
    const slugs = new Set(
      pushed.map((scan) => scan.activity_run_agent_slug || null)
    );
    if (slugs.size === 1 && !slugs.has(null)) {
      return { agentSlug: [...slugs][0] };
    }
    if (pushed.length > 1) {
      return {
        contextNote:
          "These runs do not share one known agent, so the website default is preselected. Push one agent's runs at a time to preselect its recipients.",
      };
    }
    return {};
  };

  // Prefer the live scans prop over a snapshot captured when the checkbox was
  // checked — otherwise a title edit that refetches the list leaves push /
  // copy / merge using the stale name until a full page reload.
  const resolveFreshScan = useCallback(
    (scan) => {
      if (!scan) return scan;
      const scanId = getPrimaryScanId(scan);
      if (!scanId) return scan;
      const fresh = scans.find((s) => getPrimaryScanId(s) === scanId);
      if (!fresh) return scan;
      // Only allocate a new object when metadata the push path cares about changed
      if (
        fresh.title === scan.title &&
        (fresh.scan_status || null) === (scan.scan_status || null) &&
        (fresh.excel_report_uri || null) === (scan.excel_report_uri || null)
      ) {
        return scan;
      }
      return { ...scan, ...fresh };
    },
    // getPrimaryScanId is stable (closure over scan id fields only)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scans]
  );

  useEffect(() => {
    setPushSelections((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      let changed = false;
      const next = {};
      for (const key of keys) {
        const selection = prev[key];
        const freshScan = resolveFreshScan(selection.scan);
        if (freshScan !== selection.scan) {
          changed = true;
          next[key] = { ...selection, scan: freshScan };
        } else {
          next[key] = selection;
        }
      }
      return changed ? next : prev;
    });
  }, [resolveFreshScan]);

  // State for progress modal
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressState, setProgressState] = useState({
    currentStep: 0,
    totalSteps: 0,
    currentScan: null,
    currentIssue: null,
    scanName: "",
    isComplete: false,
    error: null,
  });

  // Production push finalizes with notify:false; recipients (if any) are
  // reviewed/approved afterwards in a single queue before anything is sent.
  const [pendingReview, setPendingReview] = useState(null); // { recipientEmails, pushedScans: [{ scanId, label }] }
  const [isReviewOpen, setIsReviewOpen] = useState(false);

  // --- New state for delete/merge modals ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [mergeError, setMergeError] = useState(null);

  // --- State for consolidation ---
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [consolidateLoading, setConsolidateLoading] = useState(false);
  const [consolidateError, setConsolidateError] = useState(null);
  const [consolidateSuccess, setConsolidateSuccess] = useState(null);

  // --- State for delete all issues by status ---
  const [showDeleteAllIssuesModal, setShowDeleteAllIssuesModal] =
    useState(false);
  const [deleteAllIssuesLoading, setDeleteAllIssuesLoading] = useState(false);
  const [deleteAllIssuesError, setDeleteAllIssuesError] = useState(null);
  const [deleteAllIssuesData, setDeleteAllIssuesData] = useState({
    scan: null,
    status: null,
    issues: [],
  });
  const [deleteAllIssuesProgress, setDeleteAllIssuesProgress] = useState({
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
    isComplete: false,
  });
  const [deleteAllIssuesTimeoutId, setDeleteAllIssuesTimeoutId] =
    useState(null);

  // --- State for copy to new scan ---
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyError, setCopyError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [showCopyProgressModal, setShowCopyProgressModal] = useState(false);
  const [copyProgressState, setCopyProgressState] = useState({
    currentStep: 0,
    totalSteps: 0,
    scanCreation: null,
    currentIssue: null,
    isComplete: false,
    error: null,
  });

  // Helper to determine if a scan is selected (comparing by various possible ID fields)
  const isScanSelected = (scan) => {
    if (!selectedScan) return false;

    // Check all possible ID combinations
    return (
      selectedScan.id === scan.id ||
      selectedScan.id === scan.scan_id ||
      selectedScan.scan_id === scan.id ||
      selectedScan.scan_id === scan.scan_id
    );
  };

  // Make sure we're passing the full scan object when selecting
  const handleScanSelect = (scan) => {
    if (onScanSelect) {
      // Normalize the scan object to ensure consistent ID fields
      const normalizedScan = {
        ...scan,
        id: getPrimaryScanId(scan),
        scan_id: scan.scan_id || scan.id || scan.console_scan_id,
      };

      // Pass the normalized scan to the parent component
      onScanSelect(normalizedScan);
    }
  };

  // Group scans by status
  const groupScansByStatus = () => {
    // Define the status order
    const statusOrder = ["scanning", "to_review", "solving", "completed"];

    // Normalize scan objects to ensure they have consistent IDs
    const normalizedScans = normalizeScans(scans);

    // Group scans
    const grouped = normalizedScans.reduce((acc, scan) => {
      const status = scan.scan_status || "other";
      if (!acc[status]) acc[status] = [];
      acc[status].push(scan);
      return acc;
    }, {});

    // Sort scans within each group by start_time or updated_at (latest to oldest)
    Object.keys(grouped).forEach((status) => {
      grouped[status].sort((a, b) => {
        const dateA = new Date(a.start_time || a.updated_at || 0);
        const dateB = new Date(b.start_time || b.updated_at || 0);
        return dateB - dateA; // Latest first (descending order)
      });
    });

    // Create sorted result with status order
    const result = [];

    // First add the ordered statuses
    statusOrder.forEach((status) => {
      if (grouped[status] && grouped[status].length > 0) {
        result.push({
          status,
          scans: grouped[status],
        });
      }
    });

    // Then add any remaining statuses
    Object.keys(grouped).forEach((status) => {
      if (!statusOrder.includes(status) && grouped[status].length > 0) {
        result.push({
          status,
          scans: grouped[status],
        });
      }
    });

    return result;
  };

  const groupScansByDate = () => {
    const normalizedScans = normalizeScans(scans);
    const sorted = [...normalizedScans].sort((a, b) => {
      const dateA = new Date(a.start_time || a.updated_at || 0);
      const dateB = new Date(b.start_time || b.updated_at || 0);
      return dateB - dateA;
    });

    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    sorted.forEach((scan) => {
      const d = new Date(scan.start_time || scan.updated_at || 0);
      d.setHours(0, 0, 0, 0);
      let label;
      if (d.getTime() === today.getTime()) label = "Today";
      else if (d.getTime() === yesterday.getTime()) label = "Yesterday";
      else label = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
      if (!label || label === "Invalid Date") label = "Unknown";
      if (!grouped[label]) grouped[label] = [];
      grouped[label].push(scan);
    });

    return Object.entries(grouped).map(([label, scans]) => ({ status: label, scans }));
  };

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return "Other";

    // Convert snake_case or camelCase to Title Case with spaces
    return status
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .replace(/^\w/, (c) => c.toUpperCase())
      .trim();
  };

  // Wrapper for onScanExpand that normalizes IDs
  const handleScanExpand = (scan) => {
    if (onScanExpand) {
      // Ensure the scan has both ID fields
      const normalizedScan = {
        ...scan,
        id: getPrimaryScanId(scan),
        scan_id: scan.scan_id || scan.id || scan.console_scan_id,
      };
      // Return the promise so ScanListItem can use it
      return onScanExpand(normalizedScan);
    }
    return Promise.resolve([]); // Return an empty promise if no onScanExpand
  };

  // Wrapper for onDeleteIssues that normalizes IDs
  const handleDeleteIssues = (scanId, issueIds) => {
    if (onDeleteIssues) {
      onDeleteIssues(scanId, issueIds);
    }
  };

  // Generate a unique key for each scan
  const getScanKey = (scan) => {
    return getPrimaryScanId(scan) || `scan-${Math.random()}`;
  };

  // Count total selected issues across all selected scans
  const getTotalSelectedIssuesCount = () => {
    return Object.values(pushSelections).reduce((total, selection) => {
      return total + (selection.issues?.length || 0);
    }, 0);
  };

  // Toggle scan selection for pushing
  const handleTogglePushSelect = (scan, isChecked) => {
    console.log("Toggle push select - scan:", scan, "isChecked:", isChecked);

    if (!scan) {
      console.error("Scan is undefined in handleTogglePushSelect");
      return;
    }

    const key = getPrimaryScanId(scan);
    console.log("Toggle push select - key:", key);

    setPushSelections((prev) => {
      if (!isChecked) {
        const { [key]: _omit, ...rest } = prev;
        console.log("Removing selection for key:", key);
        return rest;
      }
      const newSelection = { scan, issues: prev[key]?.issues || [] };
      console.log("Adding selection for key:", key, "selection:", newSelection);
      return { ...prev, [key]: newSelection };
    });
  };

  // Track issue selection per scan
  const handleIssueSelectionChange = (scanId, issuesArr) => {
    setPushSelections((prev) => {
      if (!issuesArr || issuesArr.length === 0) {
        // Remove scan from pushSelections if no issues selected
        const { [scanId]: _omit, ...rest } = prev;
        return rest;
      }
      
      // Always prefer the live scans prop so title/metadata edits are not stale
      const freshFromList = scans.find((s) => getPrimaryScanId(s) === scanId);
      const scan = freshFromList || prev[scanId]?.scan;

      return {
        ...prev,
        [scanId]: { scan, issues: issuesArr },
      };
    });
  };

  // Helper function to get issues for a scan
  const getIssuesForScan = async (scan) => {
    console.log("Getting issues for scan:", scan);

    // Add null check for scan
    if (!scan) {
      console.error("Scan is undefined or null");
      return [];
    }

    // First try selected issues from pushSelections
    const scanKey = getPrimaryScanId(scan);
    console.log("Scan key:", scanKey);

    const selectedIssues = pushSelections[scanKey]?.issues || [];

    if (selectedIssues.length > 0) {
      console.log("Using selected issues:", selectedIssues);
      return selectedIssues;
    }

    // If no selected issues in pushSelections, check if scan has issues attached
    const scanIssues = scan.issues || [];
    if (scanIssues.length > 0) {
      console.log("Using all scan issues:", scanIssues);
      return scanIssues;
    }

    // If scan doesn't have issues loaded, try to fetch them
    console.log("No issues found, attempting to fetch...");
    try {
      if (onScanExpand) {
        const fetchedIssues = await onScanExpand(scan);
        console.log("Fetched issues:", fetchedIssues);
        if (fetchedIssues && fetchedIssues.length > 0) {
          return fetchedIssues;
        }
      }
    } catch (error) {
      console.error("Failed to fetch issues:", error);
    }

    // Last resort: return empty array but log warning
    console.warn(`No issues found for scan ${scanKey} (${scan.title})`);
    return [];
  };

  // Bulk push handler with progress modal
  const performBulkPush = async (recipientEmails) => {
    const selections = Object.values(pushSelections);
    console.log("Push selections:", selections);
    console.log("Push selections keys:", Object.keys(pushSelections));

    if (!selections.length) {
      console.warn("No selections found for push");
      return;
    }

    // Calculate total steps: scan creation + issues for each scan
    const scanStepData = [];
    for (const selection of selections) {
      console.log("Processing selection:", selection);

      // Check if selection has scan property
      if (!selection || !selection.scan) {
        console.error("Selection missing scan property:", selection);
        continue;
      }

      const scan = resolveFreshScan(selection.scan);
      const issues = await getIssuesForScan(scan);
      scanStepData.push({ scan, issues });
    }

    const BATCH_SIZE = 100;
    const useBatching = environment === ENVIRONMENTS.STAGING;
    // staging->prod also runs a journey-push step per scan (DB + S3 artifacts)
    const journeyStepsPerScan = environment === ENVIRONMENTS.STAGING ? 1 : 0;
    // staging->prod ends with ONE finalize step (per-scan Activity trace
    // transfer + a single prod regroup) after all issues are pushed.
    const finalizeSteps =
      environment === ENVIRONMENTS.STAGING && scanStepData.length ? 1 : 0;
    const totalSteps =
      scanStepData.reduce(
        (total, { issues }) =>
          total + 1 + journeyStepsPerScan + (useBatching ? Math.ceil(issues.length / BATCH_SIZE) : issues.length),
        0
      ) + finalizeSteps;

    console.log("Progress calculation:", {
      selections: scanStepData.length,
      totalSteps,
      useBatching,
      breakdown: scanStepData.map(({ scan, issues }) => ({
        scanName: scan.title,
        steps: 1 + (useBatching ? Math.ceil(issues.length / BATCH_SIZE) : issues.length),
      })),
    });

    // Initialize progress modal
    setProgressState({
      currentStep: 0,
      totalSteps,
      currentScan: null,
      currentJourneys: null,
      currentIssue: null,
      currentFinalize: null,
      scanName: "",
      isComplete: false,
      error: null,
      warnings: [],
    });
    setShowProgressModal(true);
    setIsReviewOpen(false);

    try {
      if (environment === ENVIRONMENTS.PRE_STAGING) {
        // Check if staging website ID is available
        if (!stagingWebsiteId) {
          const errorMsg =
            "Cannot push to staging: No staging website ID found. Make sure the console website has a staging website configured.";
          setProgressState((prev) => ({ ...prev, error: errorMsg }));
          return;
        }
        let currentStep = 0;
        // Push to STAGING (platform endpoints, useProd: false)
        for (const { scan, issues } of scanStepData) {
          if (!scan) {
            console.error("Scan is undefined in bulk push loop");
            continue;
          }
          const scanId = getPrimaryScanId(scan);
          if (!scanId) {
            console.error("Could not get scan ID for scan:", scan);
            continue;
          }
          const scanName = scan.title || `Scan ${scanId}`;
          // Update progress: Creating scan
          currentStep++;
          const scanStep = currentStep;
          setProgressState((prev) => ({
            ...prev,
            currentStep: scanStep,
            currentScan: { status: "progress" },
            currentIssue: null,
            scanName,
          }));
          // Prepare the scan data for staging API
          const stagingScan = {
            scan_id: scanId,
            website_id: stagingWebsiteId,
            title: scan.title,
            scan_status: scan.scan_status || "to_review",
            console_website_id: scan.console_website_id,
          };
          let targetScanId = scanId;
          try {
            const created = await createPlatformScan({
              scan: stagingScan,
              useProd: false,
            }).unwrap();
            targetScanId = created.scan_id || created.id || scanId;
          } catch (err) {
            // Platform returns 409 when scan_id already exists — only then update.
            if (!isScanAlreadyExistsError(err)) {
              throw err;
            }
            await updatePlatformScan({
              scanId,
              scan: stagingScan,
              useProd: false,
              websiteId: stagingWebsiteId,
            }).unwrap();
            targetScanId = scanId;
          }
          // Mark scan creation complete
          setProgressState((prev) => ({
            ...prev,
            currentScan: { status: "complete" },
          }));
          // Push each issue individually with progress updates
          for (let i = 0; i < issues.length; i++) {
            const issue = issues[i];
            const issueTitle =
              issue.title ||
              issue.visible_issue_content?.title ||
              issue.created_issue_content?.title ||
              `Issue ${i + 1}`;
            // Update progress: Pushing issue
            currentStep++;
            const issueStep = currentStep;
            setProgressState((prev) => ({
              ...prev,
              currentStep: issueStep,
              currentIssue: {
                status: "progress",
                index: i + 1,
                total: issues.length,
                title: issueTitle,
              },
            }));
            try {
              await upsertPlatformIssue({
                scanId: targetScanId,
                issue: {
                  scan_id: targetScanId,
                  issue_id:
                    issue.issue_id || issue.id || issue.console_issue_id,
                  issue_content:
                    issue.issue_content ||
                    issue.visible_issue_content ||
                    issue.created_issue_content ||
                    issue,
                  // Include additional top-level fields
                  tags: issue.tags,
                  rank: issue.rank,
                  discard_reason: issue.discard_reason,
                },
                useProd: false,
              }).unwrap();
              setProgressState((prev) => ({
                ...prev,
                currentIssue: { ...prev.currentIssue, status: "complete" },
              }));
              await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (error) {
              console.error(`Failed to push issue ${issueTitle}:`, error);
              throw new Error(
                `Failed to push issue "${issueTitle}": ${formatMutationError(
                  error,
                  "unknown error"
                )}`
              );
            }
          }
        }
        setProgressState((prev) => ({
          ...prev,
          isComplete: true,
          currentScan: null,
          currentIssue: null,
        }));
        return;
      }

      if (environment === ENVIRONMENTS.STAGING) {
        // Check if production website ID is available
        if (!productionWebsiteId) {
          const errorMsg =
            "Cannot push to production: No production website ID found. Make sure the console website has a production website configured.";
          setProgressState((prev) => ({ ...prev, error: errorMsg }));
          return;
        }

        let currentStep = 0;
        // Prod scan IDs that were successfully pushed — used by the single
        // finalize step (Activity trace transfer + regroup) after the loop.
        const pushedScanIds = [];
        // Parallel {scanId, label} list — used to build the review queue
        // (prepare + send is deferred to that queue, never done here).
        const pushedScanInfo = [];

        // Push to PRODUCTION (platform endpoints)
        for (const { scan, issues } of scanStepData) {
          if (!scan) {
            console.error("Scan is undefined in bulk push loop");
            continue;
          }

          const scanId = getPrimaryScanId(scan);
          if (!scanId) {
            console.error("Could not get scan ID for scan:", scan);
            continue;
          }

          const scanName = scan.title || `Scan ${scanId}`;

          // Update progress: Creating scan
          currentStep++;
          const scanStep = currentStep;
          setProgressState((prev) => ({
            ...prev,
            currentStep: scanStep,
            currentScan: { status: "progress" },
            currentIssue: null,
            scanName,
          }));

          // Prepare the scan data for production API
          // Note: console_website_id is not accepted by the production API schema
          const productionScan = {
            scan_id: scanId,
            website_id: productionWebsiteId,
            title: scan.title,
            scan_status: scan.scan_status || "to_review",
          };

          console.log("Production scan payload:", productionScan);

          // Track the actual scan ID to use for issues
          let targetScanId = scanId;

          try {
            const created = await createPlatformScan({
              scan: productionScan,
              useProd: true,
            }).unwrap();
            console.log("Created scan in production:", created);
            targetScanId = created.scan_id || created.id || scanId;
          } catch (err) {
            // Platform returns 409 when scan_id already exists — only then update.
            // Other failures (e.g. 404 website not found) must surface, not masquerade
            // as "scan exists" (ZENYT-CONSOLE-FRONTEND-PROD-6).
            if (!isScanAlreadyExistsError(err)) {
              throw err;
            }
            console.log("Scan exists, updating:", err);
            await updatePlatformScan({
              scanId,
              scan: productionScan,
              useProd: true,
              websiteId: productionWebsiteId,
            }).unwrap();
            targetScanId = scanId;
          }

          // Mark scan creation complete
          setProgressState((prev) => ({
            ...prev,
            currentScan: { status: "complete" },
          }));

          // Push journeys (DB test_cases/test_results + S3 artifacts) staging -> prod.
          // Non-fatal: failures become warnings; we still continue with issues.
          currentStep++;
          const journeyStep = currentStep;
          setProgressState((prev) => ({
            ...prev,
            currentStep: journeyStep,
            currentJourneys: { status: "progress", label: "Pushing journeys (DB + S3 artifacts)" },
          }));
          try {
            const journeyResult = await pushScanJourneysToProd({
              scanId: targetScanId,
              prodWebsiteId: productionWebsiteId,
            }).unwrap();
            console.log(`Journey push for ${scanName}:`, journeyResult);

            const journeyWarnings = [];
            if (journeyResult?.skipped) {
              setProgressState((prev) => ({
                ...prev,
                currentJourneys: {
                  status: "skipped",
                  label: `No journey data to push (${journeyResult.reason || "non-agentic scan"})`,
                },
              }));
            } else {
              const failedCount = journeyResult?.s3_failed?.length || 0;
              if (failedCount > 0) {
                journeyWarnings.push(
                  `${scanName}: ${failedCount} journey artifact${failedCount === 1 ? "" : "s"} failed to copy to prod S3.`
                );
              }
              setProgressState((prev) => ({
                ...prev,
                currentJourneys: {
                  status: "complete",
                  label: `Journeys pushed (${journeyResult?.test_cases_count || 0} test cases, ${journeyResult?.s3_copied || 0} S3 objects${failedCount ? `, ${failedCount} failed` : ""})`,
                },
                warnings: [...(prev.warnings || []), ...journeyWarnings],
              }));
            }
          } catch (journeyErr) {
            console.error(`Journey push failed for ${scanName}:`, journeyErr);
            const detail =
              journeyErr?.data?.detail || journeyErr?.message || "unknown error";
            setProgressState((prev) => ({
              ...prev,
              currentJourneys: {
                status: "skipped",
                label: `Journey push failed: ${detail}`,
              },
              warnings: [
                ...(prev.warnings || []),
                `${scanName}: journey push failed (${detail}). Issues will still be pushed.`,
              ],
            }));
          }

          console.log(`Found ${issues.length} issues for scan ${scanName}`);

          const totalBatches = Math.ceil(issues.length / BATCH_SIZE);

          for (let b = 0; b < totalBatches; b++) {
            const batchStart = b * BATCH_SIZE;
            const batchEnd = Math.min(batchStart + BATCH_SIZE, issues.length);
            const batch = issues.slice(batchStart, batchEnd);

            currentStep++;
            const batchStep = currentStep;
            setProgressState((prev) => ({
              ...prev,
              currentStep: batchStep,
              currentIssue: {
                status: "progress",
                index: batchEnd,
                total: issues.length,
                title: `Batch ${b + 1}/${totalBatches} (${batch.length} issues)`,
              },
            }));

            try {
              const batchPayload = batch.map((issue) => ({
                scan_id: targetScanId,
                issue_id:
                  issue.issue_id || issue.id || issue.console_issue_id,
                issue_content:
                  issue.issue_content ||
                  issue.visible_issue_content ||
                  issue.created_issue_content ||
                  issue,
                tags: issue.tags,
                rank: issue.rank,
                discard_reason: issue.discard_reason,
              }));

              const result = await bulkUpsertPlatformIssues({
                scanId: targetScanId,
                issues: batchPayload,
                useProd: true,
              }).unwrap();

              console.log(
                `Batch ${b + 1}/${totalBatches}: created=${result.created_count}, updated=${result.updated_count}`
              );

              setProgressState((prev) => ({
                ...prev,
                currentIssue: { ...prev.currentIssue, status: "complete" },
              }));
            } catch (error) {
              console.error(`Failed to push batch ${b + 1}:`, error);
              throw new Error(
                `Failed to push batch ${b + 1}/${totalBatches}: ${formatMutationError(
                  error,
                  "unknown error"
                )}`
              );
            }
          }

          pushedScanIds.push(targetScanId);
          pushedScanInfo.push({
            scanId: targetScanId,
            label: scanName,
            // Notification history is environment-specific. This flow has
            // not fetched production status, so staging history must never
            // turn the production send into a resend.
            hasBeenNotified: false,
          });

          console.log(
            `Successfully pushed scan "${scanName}" with ${issues.length} issues to production`
          );
        }

        // Finalize ONCE after all issues: transfer each scan's Activity trace
        // (check_steps -> "pages seen") and trigger the prod regroup so the
        // bulk-upserted issues surface as findings/pattern cards. Non-fatal:
        // issues are already in prod, so failures are warnings only.
        if (pushedScanIds.length) {
          currentStep++;
          setProgressState((prev) => ({
            ...prev,
            currentStep,
            currentIssue: null,
            currentFinalize: {
              status: "progress",
              label: "Finalizing (Activity traces + grouping)",
            },
          }));
          try {
            // notify:false — production push + finalize never sends email
            // directly. Recipients (if any) are reviewed/approved in a single
            // queue after everything below completes, then sent from there.
            const finalizeResult = await finalizeProdPush({
              prodWebsiteId: productionWebsiteId,
              scanIds: pushedScanIds,
              notify: false,
            }).unwrap();
            console.log("Finalize result:", finalizeResult);

            const finalizeWarnings = [];
            const failedTraces = (finalizeResult?.traces || []).filter(
              (t) => !t.transferred
            );
            for (const t of failedTraces) {
              finalizeWarnings.push(
                `Scan ${t.scan_id}: Activity trace not transferred (${t.reason || "unknown"}).`
              );
            }
            const regroup = finalizeResult?.regroup || {};
            if (regroup.error) {
              finalizeWarnings.push(
                `Prod grouping not triggered (${regroup.error}). Findings may not appear until a regroup runs.`
              );
            }

            const transferredCount =
              (finalizeResult?.traces || []).filter((t) => t.transferred)
                .length;
            setProgressState((prev) => ({
              ...prev,
              currentFinalize: {
                status: "complete",
                label: `Finalized (${transferredCount} trace${transferredCount === 1 ? "" : "s"} transferred${regroup.triggered ? ", regroup scheduled" : ""})`,
              },
              warnings: [...(prev.warnings || []), ...finalizeWarnings],
            }));
          } catch (finalizeErr) {
            console.error("Finalize step failed:", finalizeErr);
            const detail =
              finalizeErr?.data?.detail ||
              finalizeErr?.message ||
              "unknown error";
            setProgressState((prev) => ({
              ...prev,
              currentFinalize: {
                status: "skipped",
                label: `Finalize failed: ${detail}`,
              },
              warnings: [
                ...(prev.warnings || []),
                `Finalize (traces + grouping) failed (${detail}). Issues were pushed; run a prod regroup to surface findings.`,
              ],
            }));
          }
        }

        // Mark as complete
        setProgressState((prev) => ({
          ...prev,
          isComplete: true,
          currentScan: null,
          currentIssue: null,
        }));

        // Queue up the review-and-approve flow when recipients were chosen
        // and at least one scan made it to production; otherwise nothing to
        // review (push still fully completed either way).
        if (pushedScanInfo.length && recipientEmails?.length) {
          setPendingReview({
            recipientEmails,
            pushedScans: pushedScanInfo,
            pendingCount: pushedScanInfo.length,
          });
        }

        console.log("All scans pushed successfully!");
      }

      // DON'T clear selections immediately - let user see completion first
    } catch (err) {
      console.error("Bulk push failed:", err);
      const detail = formatMutationError(err, "Unknown error occurred during push");
      if (shouldReportMutationFailure(err)) {
        captureException(toCaptureError(err, detail), {
          production_push: {
            production_website_id: productionWebsiteId,
            selected_scan_count: Object.keys(pushSelections).length,
            status: err?.status ?? null,
          },
        });
      }
      setProgressState((prev) => ({
        ...prev,
        error: detail,
      }));
    }
  };

  const handleBulkPushClick = () => {
    if (pendingReview) {
      setIsReviewOpen(true);
      return;
    }
    if (environment === ENVIRONMENTS.STAGING && productionWebsiteId) {
      setShowPushRecipientsModal(true);
      return;
    }
    performBulkPush(undefined);
  };

  // Close progress modal
  const handleCloseProgressModal = () => {
    setShowProgressModal(false);
    // Clear selections only when modal is closed
    setPushSelections({});
    setProgressState({
      currentStep: 0,
      totalSteps: 0,
      currentScan: null,
      currentIssue: null,
      scanName: "",
      isComplete: false,
      error: null,
    });
  };

  // Open the notification review queue for the scans just pushed to prod.
  const handleReviewNotifications = () => {
    setIsReviewOpen(true);
  };

  const handleCloseReviewQueue = () => {
    setIsReviewOpen(false);
  };

  const handleReviewQueueComplete = () => {
    setIsReviewOpen(false);
    setPendingReview(null);
  };

  const handlePendingReviewCountChange = useCallback((pendingCount) => {
    setPendingReview((prev) =>
      prev ? { ...prev, pendingCount } : prev
    );
  }, []);

  // --- Handler for deleting selected scans / Hub run-only rows ---
  const handleDeleteSelectedScans = async () => {
    setActionLoading(true);
    setDeleteError(null);
    const useProd = environment === ENVIRONMENTS.PRODUCTION;
    const websiteId = useProd ? productionWebsiteId : stagingWebsiteId;
    try {
      for (const { scan } of Object.values(pushSelections)) {
        const runId = scan?.activity_run_id || null;
        // Hub-only / hollow scan_ids rows: delete the activity run, not V1.
        if (scan?.activity_run_only) {
          const activityRunId = runId || getPrimaryScanId(scan);
          if (!activityRunId) {
            throw new Error("No activity run ID found for selected Hub run");
          }
          if (!websiteId) {
            throw new Error(
              "Website ID is required to delete Hub activity runs"
            );
          }
          await deletePlatformActivityRun({
            runId: activityRunId,
            websiteId,
            useProd,
          }).unwrap();
          continue;
        }

        const scanId = getPrimaryScanId(scan);
        try {
          await deletePlatformScan({ scanId, useProd }).unwrap();
        } catch (err) {
          // Orphan activity row keyed by a missing scan_id, or scan already
          // gone — fall back to Hub run delete / treat 404 as success.
          if (err?.status === 404 && runId) {
            if (!websiteId) {
              throw new Error(
                "Website ID is required to delete Hub activity runs"
              );
            }
            await deletePlatformActivityRun({
              runId,
              websiteId,
              useProd,
            }).unwrap();
          } else if (err?.status !== 404) {
            throw err;
          }
        }
      }
      setShowDeleteModal(false);
      setPushSelections({});
    } catch (err) {
      setDeleteError(
        formatMutationError(err, "Failed to delete selected agents")
      );
    } finally {
      setActionLoading(false);
    }
  };

  // --- Handler for merging selected scans ---
  const handleMergeSelectedScans = async () => {
    setActionLoading(true);
    setMergeError(null);
    const scanIds = Object.values(pushSelections).map((sel) =>
      getPrimaryScanId(sel.scan)
    );
    // Use staging API unless environment is PRODUCTION
    const useProd = environment === ENVIRONMENTS.PRODUCTION;
    try {
      await mergePlatformScans({ scanIds, useProd }).unwrap();
      setShowMergeModal(false);
      setPushSelections({});
    } catch (err) {
      setMergeError(err.message || "Failed to merge scans");
    } finally {
      setActionLoading(false);
    }
  };

  // --- Handler for consolidating selected scans ---
  const handleConsolidateSelectedScans = async () => {
    setConsolidateLoading(true);
    setConsolidateError(null);
    setConsolidateSuccess(null);

    const selectedScans = Object.values(pushSelections).map((sel) => sel.scan);

    // Determine target website ID and useProd based on environment
    let targetWebsiteId;
    let useProd;

    if (environment === ENVIRONMENTS.STAGING) {
      // For staging scans, consolidate within staging environment
      targetWebsiteId = stagingWebsiteId;
      useProd = false; // Target is staging
    } else if (environment === ENVIRONMENTS.PRODUCTION) {
      // For production scans, consolidate within production environment
      targetWebsiteId = productionWebsiteId;
      useProd = true; // Target is production
    } else {
      setConsolidateError(
        "Consolidation is only available for staging and production scans"
      );
      setConsolidateLoading(false);
      return;
    }

    if (!targetWebsiteId) {
      setConsolidateError("No target website ID available for consolidation");
      setConsolidateLoading(false);
      return;
    }

    try {
      const consolidationResults = [];

      for (const scan of selectedScans) {
        const scanId = getPrimaryScanId(scan);

        try {
          const result = await consolidateConsoleScan({
            scanId,
            useProd,
            targetWebsiteId,
          }).unwrap();

          consolidationResults.push({
            scanId,
            scanTitle: scan.title,
            status: "success",
            newScanId: result.new_scan_id,
            issuesCount: result.issues_to_process,
          });
        } catch (err) {
          consolidationResults.push({
            scanId,
            scanTitle: scan.title,
            status: "error",
            error: err.message || "Unknown error",
          });
        }
      }

      // Show success message
      const successCount = consolidationResults.filter(
        (r) => r.status === "success"
      ).length;
      const errorCount = consolidationResults.filter(
        (r) => r.status === "error"
      ).length;

      if (successCount > 0) {
        setConsolidateSuccess(
          `Successfully started consolidation for ${successCount} scan(s). ${
            errorCount > 0 ? `${errorCount} failed.` : ""
          }`
        );
      }

      if (errorCount > 0 && successCount === 0) {
        setConsolidateError(`Failed to consolidate ${errorCount} scan(s)`);
      }

      setShowConsolidateModal(false);
      setPushSelections({});
    } catch (err) {
      setConsolidateError(err.message || "Failed to consolidate scans");
    } finally {
      setConsolidateLoading(false);
    }
  };

  // --- Handler for copying selected issues to a new scan ---
  const handleCopyToNewScan = async () => {
    try {
      // Collect all selected issues from all scans
      const allSelectedIssues = [];
      const selections = Object.values(pushSelections);

      for (const selection of selections) {
        const issues = selection.issues || [];
        allSelectedIssues.push(...issues);
      }

      if (allSelectedIssues.length === 0) {
        setCopyError("No issues selected to copy");
        return;
      }

      const firstSelection = selections[0];
      const firstScan = firstSelection.scan;
      const useProd = environment === ENVIRONMENTS.PRODUCTION;
      const isPlatform = environment !== ENVIRONMENTS.PRE_STAGING;

      // Get scan title and logo from the first selected scan
      const scanTitle = firstScan?.title || "Copied Issues";

      // Initialize progress: 1 step for scan creation + 1 step per issue
      const totalSteps = 1 + allSelectedIssues.length;

      setCopyProgressState({
        currentStep: 0,
        totalSteps,
        scanCreation: { status: "pending", title: scanTitle },
        currentIssue: null,
        isComplete: false,
        error: null,
      });

      setShowCopyModal(false);
      setShowCopyProgressModal(true);

      let newScan;
      let newScanId;

      if (isPlatform) {
        // For platform environments (staging/production), use platform APIs
        const websiteId = useProd ? productionWebsiteId : stagingWebsiteId;

        if (!websiteId) {
          setCopyProgressState((prev) => ({
            ...prev,
            error: "Could not determine website ID for new scan",
          }));
          return;
        }

        // Step 1: Create the scan
        setCopyProgressState((prev) => ({
          ...prev,
          scanCreation: { status: "progress", title: scanTitle },
        }));

        newScan = await createPlatformScan({
          scan: {
            website_id: websiteId,
            scan_status: "to_review",
            title: scanTitle,
          },
          useProd,
        }).unwrap();

        newScanId = newScan.scan_id || newScan.id;

        setCopyProgressState((prev) => ({
          ...prev,
          currentStep: 1,
          scanCreation: { status: "complete", title: scanTitle },
        }));

        // Step 2+: Copy each issue to the new scan using platform API
        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < allSelectedIssues.length; i++) {
          const issue = allSelectedIssues[i];
          const issueTitle =
            issue.title ||
            issue.visible_issue_content?.title ||
            issue.created_issue_content?.title ||
            `Issue ${i + 1}`;

          setCopyProgressState((prev) => ({
            ...prev,
            currentIssue: {
              status: "progress",
              index: i + 1,
              total: allSelectedIssues.length,
              title: issueTitle,
            },
          }));

          try {
            // Prepare issue data for creation (POST endpoint expects IssueCreateSchema)
            const issueData = {
              scan_id: newScanId,
              issue_content:
                issue.visible_issue_content ||
                issue.created_issue_content ||
                issue.issue_content ||
                issue,
            };

            await createPlatformIssue({
              issue: issueData,
              useProd,
            }).unwrap();

            successCount++;

            setCopyProgressState((prev) => ({
              ...prev,
              currentStep: prev.currentStep + 1,
              currentIssue: {
                ...prev.currentIssue,
                status: "complete",
              },
            }));
          } catch (err) {
            console.error(`Failed to copy issue:`, err);
            errorCount++;
          }
        }

        // Mark as complete
        setCopyProgressState((prev) => ({
          ...prev,
          isComplete: true,
          currentIssue: null,
        }));

        if (successCount > 0) {
          setCopySuccess(
            `Successfully created new scan with ${successCount} issue(s)${
              errorCount > 0 ? `. ${errorCount} issue(s) failed to copy.` : ""
            }`
          );
        }
      } else {
        // For console environment, use console APIs
        const consoleWebsiteId =
          firstSelection.scan?.console_website_id ||
          firstSelection.scan?.website_id;

        if (!consoleWebsiteId) {
          setCopyProgressState((prev) => ({
            ...prev,
            error: "Could not determine website ID for new scan",
          }));
          return;
        }

        // Step 1: Create the scan
        setCopyProgressState((prev) => ({
          ...prev,
          scanCreation: { status: "progress", title: scanTitle },
        }));

        newScan = await createConsoleScan({
          console_website_id: consoleWebsiteId,
          website_id: consoleWebsiteId,
          scan_status: "to_review",
          title: scanTitle,
        }).unwrap();

        newScanId = newScan.console_scan_id || newScan.id || newScan.scan_id;

        setCopyProgressState((prev) => ({
          ...prev,
          currentStep: 1,
          scanCreation: { status: "complete", title: scanTitle },
        }));

        // Step 2+: Copy each issue to the new scan using console API
        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < allSelectedIssues.length; i++) {
          const issue = allSelectedIssues[i];
          const issueContent = issue.issue_content || issue;
          const issueTitle = issueContent.title || `Issue ${i + 1}`;

          setCopyProgressState((prev) => ({
            ...prev,
            currentIssue: {
              status: "progress",
              index: i + 1,
              total: allSelectedIssues.length,
              title: issueTitle,
            },
          }));

          try {
            // Prepare issue data for creation (POST endpoint expects ConsoleIssueSchema)
            const issueData = {
              console_scan_id: newScanId,
              issue_content: issueContent,
            };

            await createConsoleIssue({
              scanId: newScanId,
              issue: issueData,
            }).unwrap();

            successCount++;

            setCopyProgressState((prev) => ({
              ...prev,
              currentStep: prev.currentStep + 1,
              currentIssue: {
                ...prev.currentIssue,
                status: "complete",
              },
            }));
          } catch (err) {
            console.error(`Failed to copy issue:`, err);
            errorCount++;
          }
        }

        // Mark as complete
        setCopyProgressState((prev) => ({
          ...prev,
          isComplete: true,
          currentIssue: null,
        }));

        if (successCount > 0) {
          setCopySuccess(
            `Successfully created new scan with ${successCount} issue(s)${
              errorCount > 0 ? `. ${errorCount} issue(s) failed to copy.` : ""
            }`
          );
        }
      }

      // Clear selections
      setPushSelections({});
    } catch (err) {
      setCopyProgressState((prev) => ({
        ...prev,
        error: err.message || "Failed to create new scan",
      }));
    }
  };

  // Handler to close copy progress modal
  const handleCloseCopyProgressModal = () => {
    setShowCopyProgressModal(false);
    setCopyProgressState({
      currentStep: 0,
      totalSteps: 0,
      scanCreation: null,
      currentIssue: null,
      isComplete: false,
      error: null,
    });
  };

  // --- Handler for deleting all issues by status ---
  const handleDeleteAllIssuesByStatus = async () => {
    setDeleteAllIssuesLoading(true);
    setDeleteAllIssuesError(null);

    const { scan, status, issues } = deleteAllIssuesData;

    if (!scan || !status || !issues.length) {
      setDeleteAllIssuesError("Invalid data for deletion");
      setDeleteAllIssuesLoading(false);
      return;
    }

    // Initialize progress
    setDeleteAllIssuesProgress({
      current: 0,
      total: issues.length,
      success: 0,
      failed: 0,
      isComplete: false,
    });

    const isPlatform = environment !== ENVIRONMENTS.PRE_STAGING;
    const useProd = environment === ENVIRONMENTS.PRODUCTION;

    try {
      const { successCount, failedCount, successfulIssueIds, errorMessage } =
        await runBulkIssueDeletes({
          issues,
          getIssueId: (issue) =>
            issue.issue_id || issue.id || issue.console_issue_id,
          deleteOne: async (issueId) => {
            if (isPlatform) {
              await deletePlatformIssue({ issueId, useProd }).unwrap();
            } else {
              await deleteConsoleIssue(issueId).unwrap();
            }
          },
          onProgress: setDeleteAllIssuesProgress,
        });

      // Call the onDeleteIssues callback to refresh the parent component (only for successful deletions)
      if (onDeleteIssues && successfulIssueIds.length > 0) {
        const scanId = getPrimaryScanId(scan);
        onDeleteIssues(scanId, successfulIssueIds);
      }

      // If all were successful, close modal after a brief delay to show completion
      if (failedCount === 0) {
        const timeoutId = setTimeout(() => {
          setShowDeleteAllIssuesModal(false);
          setDeleteAllIssuesData({ scan: null, status: null, issues: [] });
          setDeleteAllIssuesProgress({
            current: 0,
            total: 0,
            success: 0,
            failed: 0,
            isComplete: false,
          });
          setDeleteAllIssuesTimeoutId(null);
        }, 1500);
        setDeleteAllIssuesTimeoutId(timeoutId);
      } else {
        // If there were failures, show error (with upstream detail) but don't auto-close
        setDeleteAllIssuesError(
          errorMessage ||
            `Completed with ${successCount} successful and ${failedCount} failed deletions`
        );
      }
    } catch (err) {
      setDeleteAllIssuesError(
        formatMutationError(err, "Failed to delete issues")
      );
    } finally {
      setDeleteAllIssuesLoading(false);
    }
  };

  // --- Handler to open delete all issues modal ---
  const handleOpenDeleteAllIssuesModal = (scan, status, issues) => {
    setDeleteAllIssuesData({ scan, status, issues });
    setShowDeleteAllIssuesModal(true);
    setDeleteAllIssuesError(null);
    setDeleteAllIssuesProgress({
      current: 0,
      total: 0,
      success: 0,
      failed: 0,
      isComplete: false,
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-border-subtle p-4 h-full w-full">
        <div className="animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center mb-4">
              <div className="w-6 h-6 bg-surface-neutral-subtle rounded-lg mr-2"></div>
              <div className="flex-1">
                <div className="h-3 bg-surface-neutral-subtle rounded w-3/4 mb-1"></div>
                <div className="h-2 bg-surface-neutral rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const scanGroups = sortMode === "status" ? groupScansByStatus() : groupScansByDate();
  // Hub run-only rows are selectable for delete, but have no V1 scan to
  // push / copy / merge / consolidate.
  const hasActivityRunOnlySelected = Object.values(pushSelections).some(
    (sel) => sel.scan?.activity_run_only
  );
  const scanActionsDisabled = hasActivityRunOnlySelected || actionLoading;

  return (
    <div className="bg-white rounded-2xl border border-border-subtle overflow-hidden flex flex-col w-full max-w-xs h-full max-h-[80vh]">
      <div className="border-b border-border-subtle py-2.5 px-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-body-sm font-semibold text-text-primary">Agents</h2>
          {onCreateScan && (
            <button
              onClick={onCreateScan}
              className="text-accent-purple text-body-xs font-semibold hover:underline"
            >
              + New
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          <button
            onClick={() => setSortMode("recent")}
            className={`px-2 py-1 text-caption font-medium rounded-md transition-colors ${
              sortMode === "recent" ? "bg-button-primary text-white" : "text-text-tertiary hover:bg-surface-neutral"
            }`}
          >
            Recent
          </button>
          <button
            onClick={() => setSortMode("status")}
            className={`px-2 py-1 text-caption font-medium rounded-md transition-colors ${
              sortMode === "status" ? "bg-button-primary text-white" : "text-text-tertiary hover:bg-surface-neutral"
            }`}
          >
            By Status
          </button>
          {(environment === ENVIRONMENTS.PRE_STAGING ||
            environment === ENVIRONMENTS.STAGING) && (
            <span className="text-caption text-text-tertiary ml-auto">
              Check to push
            </span>
          )}
        </div>
      </div>

      {/* Scrollable scan list area, flex-1 to fill available space */}
      <div className="overflow-y-auto flex-1 scrollbar-thin min-h-0">
        {scans.length === 0 ? (
          <div className="p-4 text-center text-body-xs text-text-tertiary">
            No agents found.&nbsp;
            {onCreateScan && (
              <button
                onClick={onCreateScan}
                className="text-accent-purple hover:underline font-semibold text-body-xs"
              >
                Create one
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border-subtle">
            {scanGroups.map((group) => (
              <div key={group.status} className="py-0.5">
                {/* Status Header */}
                <div className={`px-3 py-1.5 bg-surface-neutral text-caption font-semibold text-text-secondary ${sortMode === "status" ? "uppercase tracking-[0.06em]" : ""}`}>
                  {sortMode === "status" ? formatStatus(group.status) : group.status}
                </div>

                {/* Scans List */}
                <ul className="divide-y divide-border-subtle">
                  {group.scans.map((scan) => (
                    <ScanListItem
                      key={getScanKey(scan)}
                      scan={scan}
                      isSelected={isScanSelected(scan)}
                      onSelect={() => handleScanSelect(scan)}
                      onExpand={handleScanExpand}
                      onDeleteIssues={handleDeleteIssues}
                      onTogglePushSelect={handleTogglePushSelect}
                      onIssueSelectionChange={handleIssueSelectionChange}
                      selectedForPush={!!pushSelections[getPrimaryScanId(scan)]}
                      environment={environment}
                      onDeleteAllIssuesByStatus={handleOpenDeleteAllIssuesModal}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- PUSH/DELETE/MERGE BUTTONS FOOTER (sticky) --- */}
      {Object.keys(pushSelections).length > 0 && (
        <div className="p-2.5 border-t border-border-default bg-white flex-shrink-0 sticky bottom-0 z-10">
          <div className="text-caption font-semibold text-text-secondary mb-2">
            {Object.keys(pushSelections).length} agent{Object.keys(pushSelections).length !== 1 ? "s" : ""} selected
            {getTotalSelectedIssuesCount() > 0 && (
              <span className="text-text-tertiary font-normal"> · {getTotalSelectedIssuesCount()} finding{getTotalSelectedIssuesCount() !== 1 ? "s" : ""}</span>
            )}
            {hasActivityRunOnlySelected && (
              <span className="text-text-tertiary font-normal">
                {" "}
                · Hub run(s) without scan ID — delete only
              </span>
            )}
          </div>

          {/* Primary action: Push */}
          {(environment === ENVIRONMENTS.PRE_STAGING ||
            environment === ENVIRONMENTS.STAGING) && (
            <>
              <Tooltip
                className="block mb-1.5"
                label={
                  hasActivityRunOnlySelected
                    ? "Hub activity runs without a scan ID cannot be pushed — deselect them or delete instead."
                    : environment === ENVIRONMENTS.PRE_STAGING
                    ? "Copy the selected agent(s) and their findings to the staging environment (creates/updates the staging agent and uploads its findings)."
                    : "Copy the selected agent(s) and their findings to production: creates/updates the prod agent, transfers journeys + the Activity trace, then regroups so findings appear on the dashboard."
                }
              >
                <button
                  onClick={handleBulkPushClick}
                  disabled={scanActionsDisabled}
                  className="w-full bg-button-primary hover:bg-button-primary-hover text-white text-caption font-semibold py-2 rounded-lg transition-colors disabled:opacity-40"
                >
                  Push to{" "}
                  {environment === ENVIRONMENTS.PRE_STAGING ? "Staging" : "Production"}
                </button>
              </Tooltip>
            </>
          )}

          {/* Secondary actions row */}
          <div className="flex gap-1.5">
            {(environment === ENVIRONMENTS.STAGING ||
              environment === ENVIRONMENTS.PRODUCTION) && (
              <Tooltip
                className="flex-1"
                label={
                  hasActivityRunOnlySelected
                    ? "Hub activity runs without a scan ID cannot be consolidated."
                    : "For each selected agent, use AI to detect and merge duplicate findings into a brand-new 'Consolidated' agent. The originals are left untouched."
                }
              >
                <button
                  onClick={() => setShowConsolidateModal(true)}
                  className="w-full text-text-secondary bg-surface-neutral hover:bg-surface-neutral-subtle text-caption font-medium py-1.5 rounded-lg disabled:opacity-40 transition-colors"
                  disabled={consolidateLoading || hasActivityRunOnlySelected}
                >
                  {consolidateLoading ? "..." : "Consolidate"}
                </button>
              </Tooltip>
            )}
            <Tooltip
              className="flex-1"
              label={
                hasActivityRunOnlySelected
                  ? "Hub activity runs without a scan ID cannot be copied."
                  : "Clone the selected findings into a brand-new agent. The source agents and their findings are left unchanged."
              }
            >
              <button
                onClick={() => setShowCopyModal(true)}
                className="w-full text-text-secondary bg-surface-neutral hover:bg-surface-neutral-subtle text-caption font-medium py-1.5 rounded-lg disabled:opacity-40 transition-colors"
                disabled={
                  hasActivityRunOnlySelected ||
                  (showCopyProgressModal && !copyProgressState.isComplete)
                }
              >
                {showCopyProgressModal && !copyProgressState.isComplete ? "..." : "Copy"}
              </button>
            </Tooltip>
            <Tooltip
              className="flex-1"
              label={
                hasActivityRunOnlySelected
                  ? "Hub activity runs without a scan ID cannot be merged."
                  : "Combine 2+ selected agents into the first one selected: all findings move to it and the now-empty agents are deleted. Requires at least 2 agents."
              }
            >
              <button
                onClick={() => setShowMergeModal(true)}
                className="w-full text-text-secondary bg-surface-neutral hover:bg-surface-neutral-subtle text-caption font-medium py-1.5 rounded-lg disabled:opacity-40 transition-colors"
                disabled={
                  hasActivityRunOnlySelected ||
                  Object.keys(pushSelections).length < 2 ||
                  actionLoading
                }
              >
                {actionLoading && showMergeModal ? "..." : "Merge"}
              </button>
            </Tooltip>
            <Tooltip
              className="flex-1"
              label="Permanently delete the selected agent(s). V1 scans remove findings + Activity; Hub run-only rows delete the activity run."
            >
              <button
                onClick={() => setShowDeleteModal(true)}
                className="w-full text-severity-high bg-severity-high/5 hover:bg-severity-high/10 text-caption font-medium py-1.5 rounded-lg disabled:opacity-40 transition-colors"
                disabled={actionLoading}
              >
                {actionLoading && showDeleteModal ? "..." : "Delete"}
              </button>
            </Tooltip>
          </div>
          {deleteError && (
            <div className="text-caption text-severity-high mt-1">{deleteError}</div>
          )}
          {mergeError && (
            <div className="text-caption text-severity-high mt-1">{mergeError}</div>
          )}
          {consolidateError && (
            <div className="text-caption text-severity-high mt-1">{consolidateError}</div>
          )}
          {consolidateSuccess && (
            <div className="text-caption text-accent-green mt-1">
              {consolidateSuccess}
            </div>
          )}
          {copyError && (
            <div className="text-caption text-severity-high mt-1">{copyError}</div>
          )}
          {copySuccess && (
            <div className="text-caption text-accent-green mt-1">{copySuccess}</div>
          )}
        </div>
      )}
      {pendingReview && !isReviewOpen && (
        <div className="border-t border-border-default bg-accent-purple/5 p-2.5">
          <button
            type="button"
            data-testid="review-pending-emails-button"
            onClick={handleReviewNotifications}
            className="w-full rounded-xl bg-button-primary px-3 py-2 text-caption font-semibold text-white transition-colors hover:bg-button-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
          >
            Review pending emails ({pendingReview.pendingCount})
          </button>
        </div>
      )}
      <NotificationRecipientsModal
        isOpen={showPushRecipientsModal}
        onClose={() => setShowPushRecipientsModal(false)}
        onConfirm={(recipientEmails) => {
          setShowPushRecipientsModal(false);
          performBulkPush(recipientEmails);
        }}
        websiteId={productionWebsiteId}
        useProd
        {...(showPushRecipientsModal ? getPushRecipientContext() : {})}
        title="Push to production"
        description="Choose who should receive the run-complete email after the push finishes. The push still runs when nobody is selected."
        confirmText="Push to production"
      />
      {/* Progress Modal */}
      <PushProgressModal
        isOpen={showProgressModal}
        progress={progressState}
        onClose={handleCloseProgressModal}
        environment={environment}
        pendingReviewCount={pendingReview?.pendingCount || 0}
        onReviewNotifications={handleReviewNotifications}
      />
      {/* Notification review queue — opened on demand after a notify:false
          production push, once recipients were chosen for the pushed scans. */}
      {pendingReview && (
        <NotificationEmailPreviewModal
          isOpen={isReviewOpen}
          onClose={handleCloseReviewQueue}
          onQueueComplete={handleReviewQueueComplete}
          onPendingCountChange={handlePendingReviewCountChange}
          mode="queue"
          items={pendingReview.pushedScans.map((s) => ({
            key: s.scanId,
            scanId: s.scanId,
            useProd: true,
            firePath: "console_push",
            label: s.label,
            recipientEmails: pendingReview.recipientEmails,
            ...(s.hasBeenNotified !== undefined
              ? { hasBeenNotified: s.hasBeenNotified }
              : {}),
          }))}
        />
      )}
      {/* Copy Progress Modal */}
      <CopyProgressModal
        isOpen={showCopyProgressModal}
        progress={copyProgressState}
        onClose={handleCloseCopyProgressModal}
      />
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteSelectedScans}
        title="Delete Selected Agents"
        message={`Are you sure you want to delete ${
          Object.keys(pushSelections).length
        } selected agent(s)? Hub runs without a scan ID are removed from Activity; V1 scans also remove their findings. This cannot be undone.`}
        confirmText="Delete"
        confirmColor="red"
      />
      {/* Merge Confirmation Modal */}
      <ConfirmationModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        onConfirm={handleMergeSelectedScans}
        title="Merge Selected Agents"
        message={`Are you sure you want to merge ${
          Object.keys(pushSelections).length
        } selected agents? All findings will be reassigned to the first agent you selected, and the others will be deleted once empty.`}
        confirmText="Merge"
        confirmColor="yellow"
      />
      {/* Consolidate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConsolidateModal}
        onClose={() => setShowConsolidateModal(false)}
        onConfirm={handleConsolidateSelectedScans}
        title="Consolidate Selected Agents"
        message={`Are you sure you want to consolidate ${
          Object.keys(pushSelections).length
        } selected agent(s)? This will create new consolidated agents with merged duplicate findings in the same ${
          environment === ENVIRONMENTS.STAGING ? "staging" : "production"
        } environment.`}
        confirmText="Consolidate"
        confirmColor="green"
      />
      {/* Copy to New Scan Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        onConfirm={handleCopyToNewScan}
        title="Copy to New Agent"
        message={`Are you sure you want to copy all selected findings to a new agent? This will create a new agent in the console with copies of the selected findings.`}
        confirmText="Copy"
        confirmColor="blue"
      />

      {/* Delete All Issues by Status Modal */}
      <DeleteAllIssuesModal
        isOpen={showDeleteAllIssuesModal}
        onClose={() => {
          // Clear any pending timeout if user manually closes
          if (deleteAllIssuesTimeoutId) {
            clearTimeout(deleteAllIssuesTimeoutId);
            setDeleteAllIssuesTimeoutId(null);
          }
          setShowDeleteAllIssuesModal(false);
          setDeleteAllIssuesData({ scan: null, status: null, issues: [] });
          setDeleteAllIssuesProgress({
            current: 0,
            total: 0,
            success: 0,
            failed: 0,
            isComplete: false,
          });
          setDeleteAllIssuesError(null);
        }}
        onConfirm={handleDeleteAllIssuesByStatus}
        deleteAllIssuesData={deleteAllIssuesData}
        deleteAllIssuesLoading={deleteAllIssuesLoading}
        deleteAllIssuesError={deleteAllIssuesError}
        deleteAllIssuesProgress={deleteAllIssuesProgress}
      />
    </div>
  );
};

export default ScanList;
